import React, { useState, useEffect, useRef } from 'react';
import {
  getJobListings,
  submitJobApplication,
  getMemberJobApplications,
} from '../../../api/programmesApi';

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function jobTypeBadge(type) {
  const map = {
    'full-time':  { cls: 'bb-member-badge--blue',   label: 'Full-time' },
    'part-time':  { cls: 'bb-member-badge--teal',   label: 'Part-time' },
    'freelance':  { cls: 'bb-member-badge--purple', label: 'Freelance' },
    'volunteer':  { cls: 'bb-member-badge--yellow', label: 'Volunteer' },
  };
  const { cls, label } = map[type] || { cls: 'bb-member-badge--gray', label: type || 'Other' };
  return <span className={`bb-member-badge ${cls}`}>{label}</span>;
}

function appStatusBadge(status) {
  const map = {
    applied:    { cls: 'bb-member-badge--blue',  label: 'Applied' },
    viewed:     { cls: 'bb-member-badge--teal',  label: 'Viewed' },
    responded:  { cls: 'bb-member-badge--green', label: 'Responded' },
  };
  const { cls, label } = map[status] || { cls: 'bb-member-badge--gray', label: status };
  return <span className={`bb-member-badge ${cls}`}>{label}</span>;
}

const WORD_ACCEPT = '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// ── Apply Modal ────────────────────────────────────────────────────────────────
function ApplyModal({ job, orgId, memberId, memberName, memberEmail, onSuccess, onClose }) {
  const [coveringLetterFile, setCoveringLetterFile] = useState(null);
  const [resumeFile, setResumeFile]                 = useState(null);
  const [submitting, setSubmitting]                 = useState(false);
  const [error, setError]                           = useState('');
  const clRef  = useRef();
  const resRef = useRef();

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!coveringLetterFile) { setError('Please upload your covering letter.'); return; }
    if (!resumeFile)         { setError('Please upload your resume/CV.'); return; }
    setSubmitting(true);
    try {
      await submitJobApplication(
        orgId, job.id, job.title,
        memberId, memberName, memberEmail,
        coveringLetterFile, resumeFile,
        job.postedBy
      );
      onSuccess(job.id);
    } catch (err) {
      setError(err.message || 'Error submitting application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, maxWidth: 520, width: '100%',
        boxShadow: '0 24px 72px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ background: '#6B46C1', borderRadius: '12px 12px 0 0', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Apply: {job.title}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{job.organisation}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13, flexShrink: 0, marginLeft: 12 }}>
            ✕ Close
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <p style={{ fontSize: 13, color: '#475569', marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
            Please upload your covering letter and resume/CV in Word format (<strong>.doc</strong> or <strong>.docx</strong>).
            Your contact details will be shared with the employer upon submission.
          </p>

          {/* Covering Letter */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Covering Letter <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <div
              onClick={() => clRef.current.click()}
              style={{
                border: `2px dashed ${coveringLetterFile ? '#6B46C1' : '#D1D5DB'}`,
                borderRadius: 8, padding: '16px 20px', cursor: 'pointer',
                background: coveringLetterFile ? '#F5F3FF' : '#FAFAFA',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <span style={{ fontSize: 22 }}>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {coveringLetterFile ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#6B46C1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {coveringLetterFile.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                      {(coveringLetterFile.size / 1024).toFixed(1)} KB — click to replace
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#6B7280' }}>Click to upload covering letter (.doc / .docx)</div>
                )}
              </div>
            </div>
            <input ref={clRef} type="file" accept={WORD_ACCEPT} style={{ display: 'none' }} onChange={handleFileChange(setCoveringLetterFile)} />
          </div>

          {/* Resume */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Resume / CV <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <div
              onClick={() => resRef.current.click()}
              style={{
                border: `2px dashed ${resumeFile ? '#6B46C1' : '#D1D5DB'}`,
                borderRadius: 8, padding: '16px 20px', cursor: 'pointer',
                background: resumeFile ? '#F5F3FF' : '#FAFAFA',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <span style={{ fontSize: 22 }}>📋</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {resumeFile ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#6B46C1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {resumeFile.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                      {(resumeFile.size / 1024).toFixed(1)} KB — click to replace
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#6B7280' }}>Click to upload resume/CV (.doc / .docx)</div>
                )}
              </div>
            </div>
            <input ref={resRef} type="file" accept={WORD_ACCEPT} style={{ display: 'none' }} onChange={handleFileChange(setResumeFile)} />
          </div>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#B91C1C', marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1, background: submitting ? '#A78BFA' : '#6B46C1', color: '#fff',
                border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 14,
                fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#F1F5F9', color: '#475569', border: 'none',
                borderRadius: 8, padding: '11px 20px', fontSize: 14, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Browse Jobs Tab ────────────────────────────────────────────────────────────
function BrowseJobs({ orgId, memberId, memberName, memberEmail }) {
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [applied, setApplied]     = useState(new Set());
  const [applyJob, setApplyJob]   = useState(null);
  const [toast, setToast]         = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getJobListings(orgId, 'live');
        setJobs(data);
      } catch (err) {
        console.error('Error loading jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  useEffect(() => {
    const loadApplied = async () => {
      if (!memberId) return;
      try {
        const apps = await getMemberJobApplications(orgId, memberId);
        setApplied(new Set(apps.map((a) => a.listingId)));
      } catch {
        // non-critical
      }
    };
    loadApplied();
  }, [orgId, memberId]);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(''), 3500);
  };

  const handleApplySuccess = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setApplied((prev) => new Set([...prev, jobId]));
    setApplyJob(null);
    showToast(`Application submitted for "${job?.title}".`);
  };

  const now = new Date();
  const filtered = jobs.filter((j) => {
    if (j.deadline) {
      const deadline = j.deadline.toDate ? j.deadline.toDate() : new Date(j.deadline);
      if (deadline < now) return false;
    }
    if (typeFilter !== 'all' && j.jobType !== typeFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (
        !j.title?.toLowerCase().includes(q) &&
        !j.organisation?.toLowerCase().includes(q) &&
        !j.description?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="bb-empty-state">
        <div className="bb-loading-spinner"></div>
        <p>Loading job listings...</p>
      </div>
    );
  }

  return (
    <div>
      {applyJob && (
        <ApplyModal
          job={applyJob}
          orgId={orgId}
          memberId={memberId}
          memberName={memberName}
          memberEmail={memberEmail}
          onSuccess={handleApplySuccess}
          onClose={() => setApplyJob(null)}
        />
      )}

      <h2 className="bb-member-section-title">Browse Jobs</h2>
      <p className="bb-member-section-sub">Find employment opportunities suited to your situation.</p>

      {/* Filter bar */}
      <div className="bb-member-filter-bar">
        <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
          Job type:
        </label>
        <select
          className="bb-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 13 }}
        >
          <option value="all">All types</option>
          <option value="full-time">Full-time</option>
          <option value="part-time">Part-time</option>
          <option value="freelance">Freelance</option>
          <option value="volunteer">Volunteer</option>
        </select>
        <input
          type="text"
          className="bb-member-search"
          placeholder="Search jobs, organisations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <p className="bb-member-privacy-note" style={{ marginBottom: 14 }}>
        Poster's personal contact details are not displayed. All communication is handled through the platform.
      </p>

      {filtered.length === 0 ? (
        <div className="bb-empty-state">
          <p>No job listings found. Try adjusting your filters.</p>
        </div>
      ) : (
        <div>
          {filtered.map((job) => {
            const isExpanded     = expandedId === job.id;
            const alreadyApplied = applied.has(job.id);
            return (
              <div key={job.id} className="bb-member-job-card">
                <div
                  className="bb-member-job-card-header"
                  onClick={() => setExpandedId(isExpanded ? null : job.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>{job.title}</span>
                      {jobTypeBadge(job.type)}
                      {alreadyApplied && (
                        <span className="bb-member-badge bb-member-badge--green">Applied</span>
                      )}
                    </div>
                    {job.organisation && (
                      <div style={{ fontSize: 13, color: '#64748B', marginBottom: 4 }}>{job.organisation}</div>
                    )}
                    {job.description && (
                      <div style={{
                        fontSize: 13, color: '#475569', lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {job.description}
                      </div>
                    )}
                    {job.deadline && (
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 6 }}>
                        Deadline: {formatDate(job.deadline)}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 18, color: '#94A3B8', flexShrink: 0 }}>
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>

                {isExpanded && (
                  <div className="bb-member-job-card-expanded">
                    {job.description && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                          Full Description
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {job.description}
                        </div>
                      </div>
                    )}
                    {job.requirements && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                          Requirements
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {job.requirements}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                      {alreadyApplied ? (
                        <button
                          className="bb-member-btn bb-member-btn-sm"
                          disabled
                          style={{ opacity: 0.65, cursor: 'not-allowed' }}
                        >
                          Application Submitted
                        </button>
                      ) : (
                        <button
                          className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                          onClick={(e) => { e.stopPropagation(); setApplyJob(job); }}
                        >
                          Apply Now
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <div className={`bb-member-toast${toast.isError ? ' error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}

// ── My Applications Tab ────────────────────────────────────────────────────────
function MyJobApplications({ orgId, memberId }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getMemberJobApplications(orgId, memberId);
        setApplications(data);
      } catch (err) {
        console.error('Error loading job applications:', err);
      } finally {
        setLoading(false);
      }
    };
    if (memberId) load();
  }, [orgId, memberId]);

  if (loading) {
    return (
      <div className="bb-empty-state">
        <div className="bb-loading-spinner"></div>
        <p>Loading your applications...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="bb-member-section-title">My Job Applications</h2>
      <p className="bb-member-section-sub">Track the status of your job applications.</p>

      <p className="bb-member-privacy-note" style={{ marginBottom: 16 }}>
        Your contact information was shared with the employer via the platform when you applied.
      </p>

      {applications.length === 0 ? (
        <div className="bb-empty-state">
          <p>You have not applied to any jobs yet.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="bb-table">
            <thead>
              <tr>
                <th>Job Title</th>
                <th>Organisation</th>
                <th>Applied Date</th>
                <th>Status</th>
                <th>Covering Letter</th>
                <th>Resume / CV</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id}>
                  <td style={{ fontWeight: 500 }}>{app.listingTitle}</td>
                  <td>{app.organisation || '—'}</td>
                  <td>{formatDate(app.createdAt)}</td>
                  <td>{appStatusBadge(app.status)}</td>
                  <td>
                    {app.coveringLetterUrl
                      ? <a href={app.coveringLetterUrl} target="_blank" rel="noreferrer" style={{ color: '#6B46C1', fontSize: 13 }}>📄 View</a>
                      : <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>}
                  </td>
                  <td>
                    {app.resumeUrl
                      ? <a href={app.resumeUrl} target="_blank" rel="noreferrer" style={{ color: '#6B46C1', fontSize: 13 }}>📋 View</a>
                      : <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function JobBoardMember({ orgId, memberId, memberName, memberEmail, activeTab }) {
  return (
    <>
      {activeTab === 'Browse Jobs' && (
        <BrowseJobs
          orgId={orgId}
          memberId={memberId}
          memberName={memberName}
          memberEmail={memberEmail}
        />
      )}
      {activeTab === 'My Applications' && (
        <MyJobApplications orgId={orgId} memberId={memberId} />
      )}
    </>
  );
}

export default JobBoardMember;
